import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NavBar from '../../Components/NavBar/navbar';
import './Auth.css';
import { API_ENDPOINTS } from '../../utils/config';
import { useAuth } from '../../Context/AuthContext';

const Account = () => {
  const { user, token, login, logout } = useAuth();
  const navigate = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [tokenName, setTokenName] = useState('sdk-token');
  const [newSecret, setNewSecret] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(user);

  const [editing, setEditing] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const refreshTokens = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(API_ENDPOINTS.API_TOKENS, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setTokens(data.tokens || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [token, authHeaders]);

  const refreshProfile = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(API_ENDPOINTS.ME, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.user) {
        setProfile(data.user);
        // Sync AuthContext only when verification flag changes (avoid render loops)
        if (Boolean(data.user.email_verified) !== Boolean(user?.email_verified)) {
          login(data.user, token);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [token, authHeaders, login, user?.email_verified]);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth/login');
      return;
    }
    if (user.user_type === 'guest') {
      return;
    }
    setProfile(user);
    refreshTokens();
    refreshProfile();
  }, [user, token, navigate, refreshTokens, refreshProfile]);

  const startEdit = () => {
    setEditUsername(profile?.username || '');
    setEditEmail(profile?.email || '');
    setProfileError('');
    setError('');
    setInfo('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setProfileError('');
    setEditUsername(profile?.username || '');
    setEditEmail(profile?.email || '');
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setError('');
    setInfo('');
    setProfileSaving(true);
    try {
      const res = await fetch(API_ENDPOINTS.ME, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          username: editUsername.trim(),
          email: editEmail.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.user) {
        setProfile(data.user);
        login(data.user, token);
        setEditing(false);
        if (data.verification_sent) {
          setInfo(
            `Profile updated. We sent a confirmation email to ${data.user.email}. Open the link to verify your new address.`
          );
        } else if (data.verification_error) {
          setInfo(`Profile updated, but confirmation email could not be sent: ${data.verification_error}`);
        } else {
          setInfo('Profile updated.');
        }
      } else {
        setProfileError(data.error || 'Could not update profile.');
      }
    } catch (err) {
      setProfileError('Server not responding.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.RESEND_VERIFICATION, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.email_verified) {
          setInfo('Email already verified.');
          setProfile((p) => ({ ...p, email_verified: true }));
        } else if (data.cooldown) {
          setInfo('A confirmation email was sent recently. Please wait a minute before resending.');
        } else {
          setInfo(
            `Confirmation email sent to ${profile?.email || 'your address'}. Check your inbox (and spam folder), then open the link.`
          );
        }
      } else {
        setError(data.error || 'Could not resend verification.');
      }
    } catch (err) {
      setError('Server not responding.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setNewSecret('');
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.API_TOKENS, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: tokenName }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 201) {
        setNewSecret(data.token);
        setInfo('Copy your API token now — it will not be shown again.');
        await refreshTokens();
      } else {
        setError(data.error || 'Could not create token.');
      }
    } catch (err) {
      setError('Server not responding.');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.API_TOKEN(tokenId), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        await refreshTokens();
        setInfo('Token revoked.');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Revoke failed.');
      }
    } catch (err) {
      setError('Server not responding.');
    }
  };

  if (!user) {
    return null;
  }

  if (user.user_type === 'guest') {
    return (
      <div className="auth-page">
        <NavBar />
        <div className="auth-page-main">
          <div className="auth-container">
            <div className="auth-box">
              <h2>Account</h2>
              <p className="auth-subtitle">
                Guest sessions cannot edit profile, verify email, or create API tokens.
              </p>
              <button type="button" className="btn-primary" onClick={() => navigate('/auth/register')}>
                Create a registered account
              </button>
              <p className="auth-footer">
                <button type="button" className="btn-secondary" onClick={logout}>
                  Leave guest session
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const verified = Boolean(profile?.email_verified);

  return (
    <div className="auth-page">
      <NavBar />
      <div className="auth-page-main">
        <div className="auth-container">
          <div className="auth-box account-box">
            <div className="auth-brand">
              Evolv<span>e<sup>x</sup></span>
            </div>
            <h2>Account</h2>
            <p className="auth-subtitle">Profile, email verification, and SDK API tokens</p>

            {error && <div className="auth-error">{error}</div>}
            {info && <div className="auth-success">{info}</div>}

            <div className="account-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              <div className="profile-header-row">
                <h3 style={{ margin: 0 }}>Profile</h3>
                {!editing && (
                  <button type="button" className="btn-secondary btn-edit" onClick={startEdit}>
                    Edit
                  </button>
                )}
              </div>

              {!editing ? (
                <>
                  <p className="account-meta">
                    <strong>{profile?.username}</strong>
                    <span className={`badge ${verified ? 'badge-ok' : 'badge-warn'}`}>
                      {verified ? 'Verified' : 'Unverified'}
                    </span>
                  </p>
                  <p className="account-meta">{profile?.email}</p>
                </>
              ) : (
                <form onSubmit={handleSaveProfile} className="profile-edit-form">
                  {profileError && <div className="auth-error">{profileError}</div>}
                  <div className="form-group">
                    <label htmlFor="edit-username">Username</label>
                    <input
                      id="edit-username"
                      type="text"
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                      autoComplete="username"
                      disabled={profileSaving}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-email">Email</label>
                    <input
                      id="edit-email"
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      autoComplete="email"
                      disabled={profileSaving}
                      required
                    />
                  </div>
                  <div className="profile-edit-actions">
                    <button type="submit" className="btn-primary" disabled={profileSaving}>
                      {profileSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={profileSaving}
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="account-section">
              <h3>Email verification</h3>
              {verified ? (
                <p className="account-meta">Your email is verified. You can create API tokens below.</p>
              ) : (
                <>
                  <p className="account-meta">
                    We send a confirmation message to your inbox. Open the link in that email to verify,
                    then you can create SDK API tokens here.
                  </p>
                  <button type="button" className="btn-secondary" disabled={loading} onClick={handleResend}>
                    Resend confirmation email
                  </button>
                  <p className="auth-footer" style={{ marginTop: '0.75rem' }}>
                    Already verified?{' '}
                    <button
                      type="button"
                      onClick={refreshProfile}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2cbb5d',
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      Refresh status
                    </button>
                  </p>
                </>
              )}
            </div>

            <div className="account-section">
              <h3>API tokens</h3>
              <p className="account-meta">
                API token for evolvex SDK. Tokens are shown once at
                creation.
              </p>

              {!verified && (
                <div className="auth-banner">Verify your email to create API tokens.</div>
              )}

              <form onSubmit={handleCreateToken}>
                <div className="token-create-row form-group" style={{ marginBottom: 0 }}>
                  <input
                    type="text"
                    value={tokenName}
                    onChange={(e) => setTokenName(e.target.value)}
                    placeholder="Token name"
                    disabled={!verified || loading}
                  />
                  <button
                    type="submit"
                    className="btn-primary"
                    style={{ width: 'auto', whiteSpace: 'nowrap' }}
                    disabled={!verified || loading}
                  >
                    Create
                  </button>
                </div>
              </form>

              {newSecret && (
                <div className="token-secret" title="Copy this token now">
                  {newSecret}
                </div>
              )}

              <div style={{ marginTop: '1rem' }}>
                {(tokens || [])
                  .filter((t) => !t.revoked_at)
                  .map((t) => (
                    <div className="token-row" key={t.token_id}>
                      <div>
                        <strong>{t.name}</strong>
                        <div className="account-meta">
                          {t.prefix || 'evlx_'}… · created {t.created_at || '—'}
                        </div>
                      </div>
                      <button type="button" className="btn-danger" onClick={() => handleRevoke(t.token_id)}>
                        Revoke
                      </button>
                    </div>
                  ))}
                {tokens.filter((t) => !t.revoked_at).length === 0 && (
                  <p className="account-meta">No active tokens yet.</p>
                )}
              </div>
            </div>

            <p className="auth-footer">
              <Link to="/">Back to home</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
